import commaFormat from "@utils/commaFormat";
import { BRAND } from "@core/brand/brand-config";
import { metricRangeLabels } from "../../../company-search/enum";
import type { ProductSearchFilters } from "./types";

const MAX_VISIBLE_PILLS = 3;
const MAX_PILL_LENGTH = 14;

export type FilterPill = {
  text: string;
  tooltip?: string;
};

export type FilterPillGroup = {
  label: string;
  pills: FilterPill[];
  overflowCount?: number;
  overflowTooltip?: string;
};

const toPill = (value: string): FilterPill => (value.length > MAX_PILL_LENGTH ? { text: `${value.slice(0, MAX_PILL_LENGTH)}...`, tooltip: value } : { text: value });

const buildListGroup = (label: string, values?: string[]): FilterPillGroup | null => {
  if (!values?.length) return null;
  const visible = values.slice(0, MAX_VISIBLE_PILLS);
  const rest = values.slice(MAX_VISIBLE_PILLS);
  return {
    label,
    pills: visible.map(toPill),
    overflowCount: rest.length || undefined,
    overflowTooltip: rest.length ? rest.join(", ") : undefined,
  };
};

export const normalizeDirectionLabel = (value?: string): string => {
  if (!value) return "-";
  const normalized = value.trim().toLowerCase();
  if (normalized === "import") return "Buyers";
  if (normalized === "export") return "Suppliers";
  return value;
};

// Source Atlas has a single whole-world dataset, so there is no direct/mirror
// distinction to report. Blank there; callers drop the label and its separator
// rather than rendering an empty slot.
export const datasetLabel = (datasetType?: string): string => {
  if (BRAND.key === "source_atlas") return "";
  return datasetType === "mirror" ? "Mirror Dataset" : "Direct Dataset";
};

export const buildFilterGroups = (filters?: ProductSearchFilters, tradeDirection?: string): FilterPillGroup[] => {
  if (!filters) return [];
  const groups: FilterPillGroup[] = [];

  const jurisdictionLabel = tradeDirection === "export" ? "Country of Export" : "Country of Import";
  const jurisdictionGroup = buildListGroup(jurisdictionLabel, filters.jurisdiction);
  if (jurisdictionGroup) groups.push(jurisdictionGroup);

  const hsCodeGroup = buildListGroup("HS Code", filters.hsCode);
  if (hsCodeGroup) groups.push(hsCodeGroup);

  metricRangeLabels.forEach(({ label, key }: { label: string; key: string }) => {
    const range = filters.metrics?.[key as keyof NonNullable<ProductSearchFilters["metrics"]>];
    if (range?.from && range?.to) groups.push({ label, pills: [{ text: `${commaFormat(range.from)} to ${commaFormat(range.to)}` }] });
  });

  return groups;
};
